/**
 * ═══════════════════════════════════════════════════════════════════════
 * Queue Sort — "Happy Clinic" Algorithm  (Single Source of Truth)
 * ═══════════════════════════════════════════════════════════════════════
 *
 * Algorithm name:  HAPPY_CLINIC
 *
 *   To swap algorithms in the future, implement a new sort function with
 *   the same signature and change ACTIVE_ALGORITHM below.
 *
 * Policy (Happy Clinic):
 *   Appointment check-ins and requeued patients (within 60 min) are
 *   PRIORITIZED — they can skip the walk-in line.  But they only skip
 *   walk-ins who arrived AFTER them; walk-ins who were already ahead
 *   keep their spot.
 *
 *   Both APPOINTMENT and REQUEUED priority types participate in the
 *   same conditional merge using queue_number.  Requeued patients get
 *   a NEW high queue_number, so they naturally land after walk-ins
 *   who registered earlier.
 *
 *   After 60 min, priority expires and requeued patients become regular
 *   walk-ins sorted by their new queue_number.
 *
 * Algorithm (conditional merge):
 *   1. Separate into two groups:
 *        • priority — is_priority && not expired (APPOINTMENT + REQUEUED)
 *        • regular  — everyone else (walk-ins, expired priority)
 *   2. Sort priority by queue_number ASC, regular by queue_number ASC.
 *   3. Conditional merge:
 *        If priority queue_number < walk-in queue_number → keep natural pos.
 *        Otherwise → walk-in goes, then priority skips ahead right after.
 *        Append leftover from whichever group remains.
 *
 * Backend equivalent:  app/utils/queue_sort.py  (must stay in sync)
 */

// ═══════════════════════════════════════════════════════════════════════
// Algorithm registry — swap algorithms by changing ACTIVE_ALGORITHM
// ═══════════════════════════════════════════════════════════════════════
const ACTIVE_ALGORITHM = 'HAPPY_CLINIC';

/**
 * Returns true if a visit still has valid (non-expired) priority.
 */
function hasActivePriority(v) {
  if (!v.is_priority) return false;
  if (v.priority_expires_at) {
    try {
      return new Date(v.priority_expires_at) > new Date();
    } catch {
      return false;
    }
  }
  return true; // no expiry set → priority is permanent (appointment check-ins)
}

/**
 * "Happy Clinic" algorithm — conditional priority merge.
 */
function happyClinicSort(visits) {
  const priority = [];
  const regular = [];

  for (const v of visits) {
    if (hasActivePriority(v)) {
      priority.push(v);
    } else {
      regular.push(v);
    }
  }

  // Sort both groups by queue_number ASC
  const qn = (x) => x.queue_number || 999;
  priority.sort((a, b) => qn(a) - qn(b));
  regular.sort((a, b) => qn(a) - qn(b));

  // ── Conditional merge ─────────────────────────────────────────────
  const result = [];
  let w = 0;
  let p = 0;

  while (w < regular.length && p < priority.length) {
    if (qn(priority[p]) < qn(regular[w])) {
      // Priority patient was ahead of this walk-in → keep natural pos
      result.push(priority[p]);
      p++;
    } else {
      // Walk-in was already in line → walk-in goes, priority skips after
      result.push(regular[w]);
      result.push(priority[p]);
      w++;
      p++;
    }
  }
  while (w < regular.length) { result.push(regular[w]); w++; }
  while (p < priority.length) { result.push(priority[p]); p++; }

  return result;
}

// ═══════════════════════════════════════════════════════════════════════
// Public API — dispatches to the active algorithm
// ═══════════════════════════════════════════════════════════════════════
const ALGORITHMS = {
  HAPPY_CLINIC: happyClinicSort,
  // Future: FIFO_STRICT: fifoStrictSort,
  // Future: TIME_SLOT:   timeSlotSort,
};

/**
 * Sort queue visits using the active algorithm.
 * Currently active: HAPPY_CLINIC
 */
export function sortByQueue(visits) {
  const algo = ALGORITHMS[ACTIVE_ALGORITHM] || happyClinicSort;
  return algo(visits);
}

/**
 * Priority-aware sort for mixed-status lists (used in NextPatientBanner
 * where visits include IN_PROGRESS, QUEUE, COMPLETED, etc.).
 * Only QUEUE visits get the interleave treatment; other statuses sort
 * by queue_number normally.
 */
export function sortByQueueMixed(visits) {
  const queueVisits = visits.filter((v) => v.status === 'QUEUE');
  const nonQueue = visits.filter((v) => v.status !== 'QUEUE');

  const sorted = sortByQueue(queueVisits);

  // Non-queue visits go before (IN_PROGRESS first, then the interleaved queue)
  return [
    ...nonQueue.sort((a, b) => (a.queue_number || 0) - (b.queue_number || 0)),
    ...sorted,
  ];
}
